import React from "react";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/button";
import { UserAvatarDropdown } from "./UserAvatarDropdown";
import { NavigationDrawer } from "./NavigationDrawer";
import { useLanguage } from "~/contexts/LanguageContext";
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

interface HeaderProps {
  title?: string;
  icon?: LucideIcon;
  showBackButton?: boolean;
  onBackClick?: () => void;
  showLogo?: boolean;
  rightContent?: ReactNode;
}

// Default header controls component
export function DefaultHeaderControls() {
  // Always show user avatar in local-only mode
  return (
    <div className="flex items-center">
      <UserAvatarDropdown />
    </div>
  );
}

export function Header({ title = "WhenCanIQuit", icon, showBackButton = false, onBackClick, showLogo = false, rightContent }: HeaderProps) {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  
  // Use translated title if it's the default
  const displayTitle = title === "WhenCanIQuit" ? "WhenCanIQuit" : title;
  
  return (
    <header className="sticky top-0 z-50 w-full border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/90 shadow-sm" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="container flex h-14 items-center justify-between px-4">
        {/* App Title/Logo */}
        <div className="flex items-center space-x-2">
          {showBackButton && onBackClick && (
            <Button variant="ghost" size="sm" onClick={onBackClick} className="p-2">
              <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
            </Button>
          )}
          {showLogo && (
            <NavigationDrawer>
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 p-0"
              >
                <span className="text-sm font-bold">W</span>
              </Button>
            </NavigationDrawer>
          )}
          <div className="flex items-center gap-2">
            {icon && React.createElement(icon, { className: "h-5 w-5" })}
            <h1 className="text-sm font-bold truncate max-w-[200px]">{displayTitle}</h1>
          </div>
        </div>

        {/* Right Content - Custom or Default */}
        {rightContent || <DefaultHeaderControls />}
      </div>
    </header>
  );
}
